import { useState, useMemo } from 'react';
import { Plus, Ban } from 'lucide-react';
import { useReactTable, getCoreRowModel, type ColumnDef } from '@tanstack/react-table';
import { toast } from '@/stores/toast-store';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DataTable } from '@/components/data-table/data-table';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { useCoupons, useDeactivateCoupon, type Coupon } from '@/hooks/customer-engagement/use-coupons';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { apiErrorMessage } from '@/lib/api-error';
import { usePermissions } from '@/hooks/use-permissions';
import { CouponCreateDialog } from './coupon-create-dialog';

type ActiveFilter = 'all' | 'active' | 'inactive';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

export function CouponListSection() {
  const [filter, setFilter] = useState<ActiveFilter>('active');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [toDeactivate, setToDeactivate] = useState<Coupon | null>(null);

  const { data, isLoading, isError, error } = useCoupons(filter === 'all' ? undefined : filter === 'active');
  const deactivate = useDeactivateCoupon();
  const permissions = usePermissions();
  const canCreate = permissions.has('coupons.create');
  const canDeactivate = permissions.has('coupons.deactivate');

  const coupons = data?.data ?? [];
  const total = coupons.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => coupons.slice((page - 1) * perPage, page * perPage), [coupons, page, perPage]);

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setPage(1);
  };

  const columns: ColumnDef<Coupon>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => <span className="font-mono font-medium text-foreground">{row.original.code}</span>,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <span>{row.original.name}</span>,
    },
    {
      accessorKey: 'discountValue',
      header: 'Discount',
      cell: ({ row }) => {
        const c = row.original;
        const str =
          c.discountType === 'amount_off'
            ? formatCurrency(Number(c.discountValue))
            : c.discountType === 'percent_off'
              ? `${Number(c.discountValue)}%`
              : 'Free product';
        return <span className="whitespace-nowrap">{str}</span>;
      },
    },
    {
      accessorKey: 'scope',
      header: 'Scope',
      cell: ({ row }) => <span className="whitespace-nowrap">{row.original.scope.replace('_', ' ')}</span>,
    },
    {
      accessorKey: 'minSpend',
      header: 'Min spend',
      cell: ({ row }) => (
        <span className="whitespace-nowrap">
          {Number(row.original.minSpend) > 0 ? formatCurrency(Number(row.original.minSpend)) : '—'}
        </span>
      ),
    },
    {
      id: 'validWindow',
      header: 'Valid window',
      cell: ({ row }) => {
        const { validFrom, validTo } = row.original;
        if (!validFrom && !validTo) return <span className="text-xs text-muted-foreground">Always</span>;
        return (
          <span className="text-xs text-muted-foreground">
            {validFrom ? formatDateTime(validFrom) : 'open'} → {validTo ? formatDateTime(validTo) : 'open'}
          </span>
        );
      },
    },
    {
      accessorKey: 'globalUseCount',
      header: () => <div className="text-right">Uses</div>,
      cell: ({ row }) => (
        <div className="text-right">
          {row.original.globalUseCount}
          {row.original.firstNCap !== null && ` / ${row.original.firstNCap}`}
        </div>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
            row.original.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {row.original.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {row.original.isActive && canDeactivate && (
            <button
              type="button"
              onClick={() => setToDeactivate(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border hover:bg-orange-50"
              title="Deactivate"
              aria-label="Deactivate coupon"
            >
              <Ban className="h-4 w-4 text-orange-500" />
            </button>
          )}
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: pageRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Coupons</h2>
          <p className="text-sm text-gray-600 mt-1">
            Create discount codes for the register. Cashiers scan or type the code at checkout.
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Create
          </button>
        )}
      </div>

      <div className="flex rounded-lg border border-border w-fit">
        {(['active', 'inactive', 'all'] as ActiveFilter[]).map((f, i) => (
          <button
            key={f}
            type="button"
            onClick={() => {
              setFilter(f);
              setPage(1);
            }}
            className={`px-3 py-2 text-sm font-medium ${
              filter === f ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
            } ${i === 0 ? 'rounded-l-lg' : ''} ${i === 2 ? 'rounded-r-lg' : ''}`}
          >
            {f === 'all' ? 'All' : f === 'active' ? 'Active' : 'Inactive'}
          </button>
        ))}
      </div>

      {isError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          Failed to load coupons: {apiErrorMessage(error)}
        </div>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading...</div>
      ) : (
        !isError && (
          <>
            <DataTable table={table} columns={columns} />
            <DataTablePagination
              page={page}
              totalPages={totalPages}
              total={total}
              perPage={perPage}
              onPageChange={setPage}
              perPageOptions={PER_PAGE_OPTIONS}
              onPerPageChange={handlePerPageChange}
            />
          </>
        )
      )}

      <CouponCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <ConfirmDialog
        open={!!toDeactivate}
        title="Deactivate coupon"
        message={
          toDeactivate
            ? `Deactivate "${toDeactivate.code}"? Cashiers won't be able to apply it at the register anymore. This does not delete existing redemptions.`
            : ''
        }
        confirmLabel={deactivate.isPending ? 'Deactivating…' : 'Deactivate'}
        variant="warning"
        loading={deactivate.isPending}
        onConfirm={async () => {
          if (!toDeactivate) return;
          try {
            await deactivate.mutateAsync(toDeactivate.id);
            toast.success(`Coupon ${toDeactivate.code} deactivated`);
            setToDeactivate(null);
          } catch (e: any) {
            toast.error(e?.error?.message ?? 'Failed to deactivate');
          }
        }}
        onCancel={() => setToDeactivate(null)}
      />
    </div>
  );
}
